import React from 'react';
import BlogArticle from 'components/blog/blogArticle';
import { Post } from 'components/blog/content/styled';
import { authorKalina as author } from 'data/authors';
import img from 'img/blog/brain.png';

import img1 from 'img/blog/shop.png';
import img2 from 'img/blog/kotwica.png';
import img3 from 'img/blog/anchoringbiasex.png';
import img4 from 'img/blog/alphabet.png';
import img5 from 'img/blog/wordingbias.png';
import img6 from 'img/blog/hand.png';
import img7 from 'img/blog/progressbar.png';
import img8 from 'img/blog/revolutex.png';
import img9 from 'img/blog/socialdesirabilitybias.png';

const Content = () => {
  return (
    <Post>
      <p>We’re not as rational as we think.</p>
      <p>
        The human brain is designed to make quick and effective decisions rather than stick to facts
        at all times.
      </p>
      <p>
        Instead of acting rationally, we prefer to act fast. This may lead to better outcomes
        indeed, but it might also lead you astray. <strong>Cognitive biases</strong> can be both a
        blessing and a curse.
      </p>
      <p>With this article, you’re going to learn</p>
      <ul>
        <li>what exactly is a cognitive bias</li>
        <li>why UX designers should be well aware of different cognitive biases</li>
        <li>how both designers and users are prone to cognitive biases</li>
        <li>what are the most common cognitive biases in design</li>
      </ul>
      <p>Ready to find out how our brains work? Let’s dive in!</p>
      <ul>
        <li>
          <a href="#what-is-it">What is a cognitive bias</a>
        </li>
        <li>
          <a href="#why-cognitive-biases-matter">Why cognitive biases matter for UX designers</a>
        </li>
        <li>
          <p>
            <a href="#common-cognitive-biases">Common cognitive biases in UX design</a>
          </p>
          <ol>
            <li>
              <a href="#anchoring-bias">Anchoring bias</a>
            </li>
            <li>
              <a href="#wording-bias">Wording bias</a>
            </li>
            <li>
              <a href="#sunk-cost-fallacy">Sunk cost fallacy</a>
            </li>
            <li>
              <a href="#social-desirability-bias">Social desirability bias</a>
            </li>
          </ol>
        </li>
        <li>
          <p>
            <a href="#key-takeaways">Key takeaways and cognitive biases reading list</a>
          </p>
        </li>
      </ul>
      <h2 id="what-is-it">What is a cognitive bias</h2>

      <p>
        Although cognitive biases are not an entirely new phenomenon, the term was{' '}
        <a
          href="http://www.its.caltech.edu/~camerer/Ec101/JudgementUncertainty.pdf"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          first defined
        </a>{' '}
        by Daniel Kahneman and Amos Tversky in the 1970s. The two widely acclaimed scientists were
        researching people’s innumeracy. They found out that most subjects make decisions that are
        far from rational, especially when it comes to large numbers.
      </p>
      <p>
        Instead of sticking to facts, people tend to use mental shortcuts to estimate the outcome.
        These shortcuts are known as <strong>heuristics</strong>, which help us solve problems
        quicker, but may also lead to errors in thinking, called <strong>cognitive biases</strong>.
      </p>
      <p>
        According to{' '}
        <a
          href="https://www.interaction-design.org/literature/topics/cognitive-biases"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          Interaction Design Foundation
        </a>
        , a <strong>cognitive biasis</strong> a systematic (non-random) way in which the context
        affects judgment and decision-making. In other words, it’s all about the{' '}
        <strong>framing</strong> of information. We focus on different aspects depending on the
        surroundings. Because of that, our reasoning is not fully rational. Tversky and Kahneman
        found out that if we frame the same information differently, it may lead to different
        outcomes.
      </p>
      <p>
        Here’s a classic example of the <strong>framing bias</strong> in action:
      </p>
      <p>
        <img src={img1} alt="Pricing - framing bias" title="Example of framing bias" />
      </p>
      <p>
        $290 may appear like a high price, yet it seems like a bargain when you know that the same
        product would normally cost $400. A \$110 saving changes the perspective, doesn’t it?
      </p>
      <p>
        <a href="/signup/" target="_blank" rel="noopener noreferrer" className="action-banner">
          See how the users interact with your website. Try LiveSession for free today.
        </a>
      </p>
      <p>
        This popular case is just the tip of the iceberg. It’s a proven and tested marketing trick
        to generate more sales, but there’s much more to cognitive biases that could matter to UX
        designers. Here’s why this kind of knowledge is sure to come in handy:
      </p>
      <h2 id="why-cognitive-biases-matter">Why cognitive biases matter for UX designers</h2>

      <p>
        Before we dig deeper, it’s important to note that both the designers and the users are prone
        to cognitive biases. None of us are fully immune to heuristics and prejudices – and this is
        exactly why we should be aware of it. The same rules that affect how the users make
        decisions apply to UX designers.
      </p>
      <p>
        As we’ve already mentioned above, we’re all prone to <strong>framing</strong>. The context,
        as well as our previous experiences, all affect design decisions. All these external factors
        make us focus on specific aspects of the issue – or ignore the other ones.
      </p>
      <p>
        Kathryn Whitenton of the{' '}
        <a
          href="https://www.nngroup.com/articles/decision-framing-cognitive-bias-ux-pros/"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          Nielsen Norman Group
        </a>{' '}
        used a brilliant example to describe how this works for UX designers. Imagine that you’ve
        conducted a usability test with 20 users. The outcomes can be described in two different
        ways
      </p>
      <ul>
        <li>
          <strong>20% of users couldn’t find</strong> the function on the website
        </li>
        <li>
          <strong>80% of users found</strong> the function on the website.
        </li>
      </ul>
      <p>
        See the difference? The researchers from Nielsen Norman Group decided to test both versions
        in an online quiz. Here’s what happened: 39% of UX designers who saw the success rated voted
        for a redesign. In the case of respondents who saw the failure rate, 51% of them thought
        that the feature needs to be redesigned.
      </p>
      <p>
        This shows how framing research results and statistics in a different manner may lead to
        significantly different design decisions. On the other hand, the{' '}
        <strong>framing bias</strong> can affect the users too. It’s mostly visible in the
        perception of the pricing, what is expensive, and what can be considered a bargain.
      </p>
      <p>
        Below we’re going to describe how the most common cognitive biases apply to both the users
        and the UX designers.
      </p>
      <p>Ready to learn more?</p>
      <h2 id="common-cognitive-biases">Common cognitive biases in UX design</h2>

      <p>
        We’ve used one of the most popular biases, the <strong>framing bias</strong>, as the main
        example. Here’s a couple of other cases that are worth keeping in mind:
      </p>
      <h3 id="anchoring-bias">Anchoring bias</h3>

      <p>
        <img src={img2} alt="Anchoring bias" title="Common example of cognitive biases" />
      </p>
      <p>
        The <strong>anchoring bias</strong>(also known as the <strong>anchoring principle</strong>)
        is about relying on a single aspect and ignoring the other ones involved. Because people use
        it to make decisions, it’s also a judgment heuristic, exactly like framing.\ \
        <strong>How it works for the user</strong>: Anchoring bias can be hugely helpful in
        understanding user interfaces. It’s the bright side of this phenomenon – the user can stick
        to one clue, and therefore, learn to use the application faster. This goes in line with the{' '}
        <strong>principle of least effort</strong> and <strong>Jakob’s law</strong>. We’re
        describing these two in our in-depth article about{' '}
        <a
          href="/blog/7-psychological-principles-for-better-ux/"
          target="_blank"
          rel="noopener noreferrer"
        >
          psychological principles in UX
        </a>
        .
      </p>
      <p>
        <strong>Suggested values</strong> are also a good example here. Most non-profit websites,
        such as{' '}
        <a href="https://www.gofundme.com/" target="_blank" rel="noopener noreferrer nofollow">
          GoFundMe
        </a>
        , don’t charge a set fee for their services. Instead, they provide a suggestion:
      </p>
      <p>
        <img
          src={img3}
          alt="Example of anchoring bias"
          title="This is good practise of using suggested values"
        />
      </p>
      <p>
        <strong>How it works for the UX researcher</strong>: Because of the{' '}
        <a
          href="https://thedecisionlab.com/biases/primacy-effect/"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          primacy effect
        </a>
        , the first piece of information we receive usually becomes the anchor. It’s good to keep
        that in mind for <strong>user testing</strong>. The respondents may prefer the first version
        they were introduced to simply because it was the first one, not because it was actually
        better. One of the ways to avoid this is through{' '}
        <a href="/blog/5-tips-for-better-a-b-tests/" target="_blank" rel="noopener noreferrer">
          A/B testing
        </a>
        – you can show different version to your users in a different order.
      </p>
      <p>
        <img src={img4} alt="Try to test users" title="A/B test are good to test your interface" />
      </p>
      <h3 id="wording-bias">Wording bias</h3>

      <p>
        <img src={img5} alt="wording bias" title="Learn how to avoid wording biases" />
      </p>
      <p>
        <em>
          What is the best{' '}
          <a
            href="/blog/guide-to-qualitative-ux-research/"
            target="_blank"
            rel="noopener noreferrer"
          >
            qualitative research method
          </a>{' '}
          and why it’s session recording?
        </em>
      </p>
      <p>
        This is a classic (a little exaggerated, yes) example of the <strong>wording bias</strong>,
        which is also known as <strong>response bias</strong>. It occurs when the question itself
        influences the answer. <strong>How it works for the user</strong>: Imagine taking a survey,
        where the first question is: <em>How difficult was it for you to use this feature?</em> The
        question alone implies that using this particular feature <em>is difficult</em>, at least to
        some extent.
      </p>
      <p>
        <strong>How it works for the UX researcher</strong>: The wording bias largely affects the
        validity of surveys. Here’s how different types of biased questions can affect research
        results:
      </p>
      <ul>
        <li>
          <strong>The leading question</strong>, e.g.{' '}
          <em>Do you prefer the previous or the improved version of the app?</em> The word{' '}
          <em>improved</em> suggests the new version is better than the old one.
        </li>
        <li>
          <strong>The double-barreled question</strong>, e.g.{' '}
          <em>Are you enjoying the new inbox and text editor?</em> What if the user is a fan of the
          inbox yet they’re sceptical about the editor? In this case, the respondent has to express
          the same opinion about both features.
        </li>
        <li>
          <strong>The absolute question</strong>, which may not always work. Sometimes the yes/no
          answer misses the point. For instance, when you ask the user if they enjoy using the new
          feature, you might want to leave space for their comments and ideas. This way, you can
          gain much more in-depth insights.
        </li>
      </ul>
      <h3 id="sunk-cost-fallacy">Sunk cost fallacy</h3>

      <p>
        <img src={img6} alt="Sun cost fallacy" title="It goes in line with loss aversion" />
      </p>
      <p>
        Again, this is an example of how past decisions influence current choices.
        <strong>Sunk cost fallacy</strong> means that if we already invested a lot of resources in
        something, we’re going to keep investing more. It happens because we don’t want our efforts
        to go to waste. This is what makes us finish bad movies – we’re not likely to drop it if
        we’re already halfway through, right?
      </p>
      <p>
        The sunk cost fallacy goes in line with another phenomenon called{' '}
        <strong>loss aversion</strong>, which states that our brains consider all losses as more
        severe than gains. What’s more,{' '}
        <a
          href="https://citeseerx.ist.psu.edu/viewdoc/summary?doi=10.1.1.320.8769"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          some studies suggest
        </a>{' '}
        that losses are perceived as psychologically twice more important than gains! For instance,
        losing $100 hurts more than gaining 100$ satisfies.
      </p>
      <p>Same rules apply to user experience design.</p>
      <p>
        <strong>How it works for the user</strong>: The sunk cost bias is often a part of the user
        flow, especially when it comes to signing up for a service, or making a purchase. This
        mechanism can be triggered by different design tricks, such as:
      </p>
      <ul>
        <li>
          <strong>Progress bar</strong> – This solution encourages users to complete their actions,
          even if they require a certain commitment. Have a look at this sign-up form example from{' '}
          <a
            href="https://www.datafeedwatch.com/"
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            DataFeedWatch
          </a>
          :{' '}
          <img
            src={img7}
            alt="Progress bar is important thing"
            title="Let the customers complete their actions"
          />{' '}
          At this stage, the potential customer is most likely to drop out, as they need to provide
          payment details. The <strong>progress bar</strong>, however, indicates that it’s the final
          step of the process. It doesn’t make much sense to abandon the form now that you’ve gone
          so far, right?
        </li>
        <li>
          <strong>Sunk rewards</strong> – Used in loyalty programs, <strong>sunk rewards</strong>{' '}
          encourage the user to stick to your product. It’s a bit like collecting stamps in a cafe
          so you can get the 10th coffee for free. When you’ve got just one or two purchases left to
          get something extra, you probably won’t resist the temptation.
        </li>
        <li>
          <strong>Top-up suggestions</strong>– Have a look at this example from{' '}
          <a href="https://www.revolut.com/" target="_blank" rel="noopener noreferrer nofollow">
            Revolut
          </a>
          :{' '}
          <img
            src={img8}
            alt="Example in Revolut"
            title="That&#39;s how Revolut do top-up suggestions"
          />
        </li>
      </ul>
      <p>
        To use the service, you need to add money to your account first. But here’s the trick: you
        can’t add less than a certain amount (in this case, 20 PLN). This often means that you’re
        obliged to have more money in your account than you first needed. Because of that, you’re
        likely to use Revolut more often. What’s more, suggested top-up amounts right above the
        keyboard fulfil the same function. See the pattern?
      </p>
      <p>
        <strong>How it works for the UX designer</strong>: You’ve probably already guessed it: we
        tend to stick to bad design because of the sunk cost fallacy. If you spent long hours
        working on a feature, you would want to stick to it even if it turns out to be a bad idea.
        One of the ways to avoid this is through{' '}
        <a
          href="https://www.atlassian.com/agile"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          agile development
        </a>
        . Short sprints and iterations make it easier to apply changes along the way and avoid
        wasting resources.
      </p>
      <h3 id="social-desirability-bias">Social desirability bias</h3>

      <p>
        <img
          src={img9}
          alt="Social desirability bias"
          title="Also known as the friendliness bias"
        />
      </p>
      <p>
        This one’s about our tendency to seem likeable and be accepted.{' '}
        <strong>Social desirability bias</strong>(also known as the{' '}
        <strong>friendliness bias</strong>) encourages the user to answer in a way they think is
        expected by the researcher.
      </p>
      <p>
        <strong>How it works for the user</strong>: What’s interesting, the social desirability bias
        typically appears unconsciously. Most of the time, the respondents don’t realize that they
        respond in a kinder, more favorable manner. For instance, if you ask “How do you like the
        new dashboard?”, the user is likely to select a higher rating, just to make you feel better
        about it.
      </p>
      <p>
        <strong>How it works for the UX researcher</strong>: Now that you know how social
        desirability bias works, you might be wondering how to prevent it from happening. This bias
        can be avoided through <strong>indirect questions</strong>. Instead of asking how the user
        feels about a feature, you can ask “How do you think an average user would interact with
        this feature?”. This way, they won’t feel the urge to appear as nice and friendly.
      </p>
      <h2 id="key-takeaways">Key takeaways and cognitive biases reading list</h2>

      <p>
        The most important finding? We’re all prone to cognitive biases and the best we can do is to{' '}
        <strong>stay aware</strong>. Educating yourself about psychological principles that affect
        our minds is sure to pay off in the long run. It will help you understand user behavior, and
        it’s going to improve the quality of your work as a UX professional as well.
      </p>
      <p>Here’s our suggested reading list, if you’d like to dig deeper:</p>
      <p>
        <a
          href="https://www.amazon.com/Thinking-Fast-Slow-Daniel-Kahneman/dp/0374533555"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          Thinking, Fast and Slow
        </a>{' '}
        by Daniel Kahneman
      </p>
      <p>
        <a
          href="https://www.amazon.com/BIASES-HEURISTICS-Collection-Heuristics-Everything-ebook/dp/B07SHPYDFD"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          Biases and Heuristics
        </a>{' '}
        by Henry Priest
      </p>
      <p>
        <a
          href="https://www.amazon.com/Everyday-Bias-Identifying-Navigating-Unconscious-ebook/dp/B00NIHLAEC"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          Everyday Bias
        </a>{' '}
        by Howard J. Ross
      </p>
      <p>
        <a
          href="https://www.amazon.com/Art-Thinking-Clearly-Better-Decisions-ebook/dp/B00AJP267G"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          The Art of Thinking Clearly
        </a>{' '}
        by Rolf Dobelli
      </p>
      <p>We hope you found this inspiring. Happy learning!</p>
    </Post>
  );
};

const Wrapped = BlogArticle(Content);

export const frontmatter = {
  title: 'Cognitive Bias in UX: The User Side and The Designer Side',
  url: '/blog/cognitive-bias-ux/',
  description: `Learn how cognitive biases affect UX design decisions and user behavior. Read
  about the psychology of UX on the LiveSession blog.`,
  author,
  img,
  imgSocial: img,
  date: '2020-03-05',
  category: '',
  group: 'Design',
  timeToRead: 9,
};

export default () => (
  <Wrapped
    title={frontmatter.title}
    category={frontmatter.category}
    description={frontmatter.description}
    date={frontmatter.date}
    author={frontmatter.author}
    url={frontmatter.url}
    img={frontmatter.img}
    imgSocial={process.env.GATSBY_HOST + frontmatter.img}
    group={frontmatter.group}
    timeToRead={frontmatter.timeToRead}
  />
);
